import React, { useState, useRef, useLayoutEffect, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from "react-i18next";
import { ChevronRight, Search, Check, X } from 'lucide-react';
import { useMasterLabel } from '../../hooks/useMasterLabel';
import CountryFlag from './CountryFlag';

// Translated country label for filter rows.
const CountryRowLabel = ({ country }) => {
    const label = useMasterLabel('masterCountry', country?.countryId, 'countryName', country?.countryName || '');
    return <span className="truncate text-left">{label}</span>;
};

/**
 * Smart-positioned filter popover container.
 * Auto-detects if the popup would overflow left or right of the viewport
 * and positions accordingly (right-aligned or left-aligned).
 */
export const FilterPopover = forwardRef(({ children }, ref) => {
    const popoverRef = useRef(null);
    const [align, setAlign] = useState(null);

    useImperativeHandle(ref, () => popoverRef.current);

    useLayoutEffect(() => {
        const el = popoverRef.current;
        if (!el) return;

        const parent = el.parentElement;
        if (!parent) return;

        const parentRect = parent.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const popupWidth = 520;

        // Space available on each side of the parent button
        const spaceRight = viewportWidth - parentRect.left - 16;
        const spaceLeft = parentRect.right - 16;

        // If popup fits when left-aligned (extending right), and right-align would overflow
        if (spaceRight >= popupWidth) {
            setAlign('left');
        } else if (spaceLeft >= popupWidth) {
            setAlign('right');
        } else {
            // Neither side fits perfectly — pick the side with more space
            setAlign(spaceRight >= spaceLeft ? 'left' : 'right');
        }
    }, []);

    return (
        <div
            ref={popoverRef}
            className={`absolute z-20 top-full ${align === 'right' ? 'right-0' : 'left-0'} mt-2 bg-white shadow-2xl rounded-xl border border-gray-200 w-[520px] max-h-[60vh] overflow-hidden flex flex-col ${align === null ? 'invisible' : ''}`}
            data-testid="filter-popover"
        >
            {children}
        </div>
    );
});

/**
 * Inline option list for the right panel (gender, timezone, status, mode, etc.)
 *
 * Modes:
 *  - Single-select (default, `multiple=false`): `value` is a string; `onChange(newString)`.
 *  - Multi-select (`multiple=true`): `value` is an array of strings; `onChange(newArray)`.
 *    Clicking a row toggles its presence in the array.
 */
export const FilterOptionList = ({ options = [], value, onChange, multiple = false }) => {
    const { t } = useTranslation();
    const selectedArray = multiple ? (Array.isArray(value) ? value : []) : null;
    return (
        <div className="space-y-0.5" data-testid="filter-option-list">
            {options.map((option) => {
                const isSelected = multiple
                    ? selectedArray.includes(option.value)
                    : value === option.value;
                const handleClick = () => {
                    if (multiple) {
                        const next = isSelected
                            ? selectedArray.filter((v) => v !== option.value)
                            : [...selectedArray, option.value];
                        onChange(next);
                    } else {
                        onChange(isSelected ? '' : option.value);
                    }
                };
                return (
                    <button
                        key={option.value}
                        type="button"
                        onClick={handleClick}
                        className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm rounded-lg transition-all duration-150 cursor-pointer ${
                            isSelected
                                ? 'bg-[#4ABDBD]/10 text-[#4ABDBD] font-semibold'
                                : 'text-gray-700 hover:bg-gray-50'
                        }`}
                        data-testid={`filter-option-${option.value || 'all'}`}
                    >
                        <div className={`w-[18px] h-[18px] rounded border-2 flex items-center justify-center flex-shrink-0 transition-all duration-150 ${
                            isSelected ? 'border-[#4ABDBD] bg-[#4ABDBD]' : 'border-gray-300'
                        }`}>
                            {isSelected && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <span className="truncate text-left">{option.label}</span>
                    </button>
                );
            })}
        </div>
    );
};

/**
 * Inline country list for the right panel (with search + flags).
 *
 * Modes:
 *  - Single-select (default, `multiple=false`): `value` is a countryId string; `onChange(newId)`.
 *  - Multi-select (`multiple=true`): `value` is an array of countryIds; `onChange(newArray)`.
 */
export const FilterCountryList = ({ countries = [], value, onChange, searchPlaceholder, noResultsText, multiple = false }) => {
    const { t } = useTranslation();
    const [search, setSearch] = useState('');
    const filtered = countries.filter(c =>
        c.countryName?.toLowerCase().includes(search.toLowerCase())
    );
    const selectedArray = multiple ? (Array.isArray(value) ? value : []) : null;

    return (
        <div data-testid="filter-country-list-container">
            <div className="relative mb-2" data-testid="filter-country-search-container">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-testid="filter-country-search-icon" />
                <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={searchPlaceholder || t("common.search")}
                    className="w-full pl-8 pr-8 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-[#4ABDBD] focus:ring-1 focus:ring-[#4ABDBD]/30 placeholder-gray-400 transition-all"
                    data-testid="filter-country-search-input"
                />
                {search && (
                    <button
                        type="button"
                        onClick={() => setSearch('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-200 rounded-full transition-colors cursor-pointer"
                        data-testid="filter-country-search-clear-button"
                    >
                        <X className="w-3.5 h-3.5 text-gray-400" data-testid="filter-country-search-clear-icon" />
                    </button>
                )}
            </div>
            <div className="space-y-0.5 max-h-[200px] overflow-y-auto custom-scrollbar" data-testid="filter-country-list">
                {filtered.length === 0 ? (
                    <div className="px-3 py-6 text-center text-sm text-gray-400" data-testid="filter-country-no-results">{noResultsText || t("common.no_results")}</div>
                ) : (
                    filtered.map((c) => {
                        const isSelected = multiple
                            ? selectedArray.includes(c.countryId)
                            : value === c.countryId;
                        const handleClick = () => {
                            if (multiple) {
                                const next = isSelected
                                    ? selectedArray.filter((id) => id !== c.countryId)
                                    : [...selectedArray, c.countryId];
                                onChange(next);
                            } else {
                                onChange(isSelected ? '' : c.countryId);
                            }
                        };
                        return (
                            <button
                                key={c.countryId}
                                type="button"
                                onClick={handleClick}
                                className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm rounded-lg transition-all duration-150 cursor-pointer ${
                                    isSelected ? 'bg-[#4ABDBD]/10 text-[#4ABDBD] font-semibold' : 'text-gray-700 hover:bg-gray-50'
                                }`}
                                data-testid={`filter-country-option-${c.countryId}`}
                            >
                                <div className={`w-[18px] h-[18px] rounded border-2 flex items-center justify-center flex-shrink-0 transition-all duration-150 ${
                                    isSelected ? 'border-[#4ABDBD] bg-[#4ABDBD]' : 'border-gray-300'
                                }`}>
                                    {isSelected && <Check className="w-3 h-3 text-white" />}
                                </div>
                                <CountryFlag
                                    countryName={c.countryName}
                                    alt={c.countryName}
                                    className="w-5 h-3.5 object-cover rounded-sm shrink-0"
                                />
                                <CountryRowLabel country={c} />
                            </button>
                        );
                    })
                )}
            </div>
        </div>
    );
};

/**
 * Searchable list for the right panel (doctors, patients, etc.)
 *
 * Modes:
 *  - Single-select (default, `multiple=false`): `value` is an item object or null;
 *    `onChange(item or null)`. The "All" row clears the selection.
 *  - Multi-select (`multiple=true`): `value` is an array of selected items;
 *    `onChange(newArray)`. The "All" row clears the array.
 */
export const FilterSearchableList = ({
    items = [],
    value,
    onChange,
    labelKey = 'name',
    sublabelKey = 'surname',
    valueKey = 'id',
    searchPlaceholder,
    noResultsText,
    allLabel,
    loading = false,
    onSearch,
    multiple = false,
}) => {
    const { t } = useTranslation();
    const [search, setSearch] = useState('');
    const selectedArray = multiple ? (Array.isArray(value) ? value : []) : null;
    const selectedIds = multiple ? selectedArray.map((it) => it[valueKey]) : null;

    const handleSearchChange = (val) => {
        setSearch(val);
        if (onSearch) onSearch(val);
    };

    const filtered = onSearch
        ? items
        : items.filter((item) => {
            const label = `${item[labelKey] || ''} ${item[sublabelKey] || ''}`.toLowerCase();
            return label.includes(search.toLowerCase());
        });

    const isAllSelected = multiple ? selectedArray.length === 0 : !value;

    const handleAllClick = () => {
        if (multiple) {
            onChange([]);
        } else {
            onChange(null);
        }
    };

    return (
        <div data-testid="filter-searchable-list-container">
            <div className="relative mb-2" data-testid="filter-searchable-search-container">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-testid="filter-searchable-search-icon" />
                <input
                    type="text"
                    value={search}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    placeholder={searchPlaceholder || t("common.search")}
                    className="w-full pl-8 pr-8 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-[#4ABDBD] focus:ring-1 focus:ring-[#4ABDBD]/30 placeholder-gray-400 transition-all"
                    data-testid="filter-searchable-search-input"
                />
                {search && (
                    <button
                        type="button"
                        onClick={() => handleSearchChange('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-200 rounded-full transition-colors cursor-pointer"
                        data-testid="filter-searchable-search-clear-button"
                    >
                        <X className="w-3.5 h-3.5 text-gray-400" data-testid="filter-searchable-search-clear-icon" />
                    </button>
                )}
            </div>
            <div className="space-y-0.5 max-h-[200px] overflow-y-auto custom-scrollbar" data-testid="filter-searchable-list">
                {/* All option */}
                <button
                    type="button"
                    onClick={handleAllClick}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm rounded-lg transition-all duration-150 cursor-pointer ${
                        isAllSelected ? 'bg-[#4ABDBD]/10 text-[#4ABDBD] font-semibold' : 'text-gray-700 hover:bg-gray-50'
                    }`}
                    data-testid="filter-searchable-all-button"
                >
                    <div className={`w-[18px] h-[18px] rounded border-2 flex items-center justify-center flex-shrink-0 transition-all duration-150 ${
                        isAllSelected ? 'border-[#4ABDBD] bg-[#4ABDBD]' : 'border-gray-300'
                    }`}>
                        {isAllSelected && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <span className="truncate text-left">{allLabel || t("common.all")}</span>
                </button>

                {loading ? (
                    <div className="px-3 py-6 flex justify-center" data-testid="filter-searchable-loading">
                        <span className="h-5 w-5 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin"></span>
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="px-3 py-6 text-center text-sm text-gray-400" data-testid="filter-searchable-no-results">{noResultsText || t("common.no_results")}</div>
                ) : (
                    filtered.map((item) => {
                        const itemId = item[valueKey];
                        const isSelected = multiple
                            ? selectedIds.includes(itemId)
                            : value && value[valueKey] === itemId;
                        const label = `${item[labelKey] || ''} ${item[sublabelKey] ? item[sublabelKey] : ''}`.trim();
                        const handleClick = () => {
                            if (multiple) {
                                const next = isSelected
                                    ? selectedArray.filter((it) => it[valueKey] !== itemId)
                                    : [...selectedArray, item];
                                onChange(next);
                            } else {
                                onChange(isSelected ? null : item);
                            }
                        };
                        return (
                            <button
                                key={itemId}
                                type="button"
                                onClick={handleClick}
                                className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm rounded-lg transition-all duration-150 cursor-pointer ${
                                    isSelected ? 'bg-[#4ABDBD]/10 text-[#4ABDBD] font-semibold' : 'text-gray-700 hover:bg-gray-50'
                                }`}
                                data-testid={`filter-searchable-option-${itemId}`}
                            >
                                <div className={`w-[18px] h-[18px] rounded border-2 flex items-center justify-center flex-shrink-0 transition-all duration-150 ${
                                    isSelected ? 'border-[#4ABDBD] bg-[#4ABDBD]' : 'border-gray-300'
                                }`}>
                                    {isSelected && <Check className="w-3 h-3 text-white" />}
                                </div>
                                <span className="truncate text-left">{label || '-'}</span>
                            </button>
                        );
                    })
                )}
            </div>
        </div>
    );
};

/**
 * Filter section - data wrapper for FilterPanel
 * Provides title, isActive flag, and children to render in right panel
 */
export const FilterSection = ({ title, children, isActive = false }) => {
    const { t } = useTranslation();
    // Rendered by FilterPanel, not directly
    return null;
};

/**
 * Two-panel filter layout:
 * Left panel: category titles with chevrons
 * Right panel: content for selected category
 */
export const FilterPanel = ({ children, onClear, onApply, clearLabel, applyLabel }) => {
    const { t } = useTranslation();
    const [activeIndex, setActiveIndex] = useState(0);

    const sections = React.Children.toArray(children).filter(
        child => child?.type === FilterSection
    );

    const activeSection = sections[activeIndex];

    return (
        <div className="flex flex-col flex-1 min-h-0" data-testid="filter-panel-container">
            <div className="flex flex-1 min-h-0">
                {/* Left Panel - Category List */}
                <div className="w-2/5 border-r border-gray-100 bg-gray-50/30 overflow-y-auto custom-scrollbar" data-testid="filter-panel-categories">
                    {sections.map((section, index) => {
                        const isSelected = index === activeIndex;
                        const hasValue = section.props.isActive;
                        return (
                            <button
                                key={index}
                                type="button"
                                onClick={() => setActiveIndex(index)}
                                className={`w-full flex items-center justify-between px-3 py-3.5 text-sm font-semibold transition-all duration-200 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                                    isSelected
                                        ? 'bg-white text-[#4ABDBD] shadow-sm'
                                        : hasValue
                                            ? 'text-[#4ABDBD] hover:bg-white/60'
                                            : 'text-gray-700 hover:bg-white/60 hover:text-[#4ABDBD]'
                                }`}
                                data-testid={`filter-panel-category-${index}`}
                            >
                                <span className="flex items-center gap-1.5">
                                    {section.props.title}
                                    {hasValue && (
                                        <span className="w-1.5 h-1.5 rounded-full bg-[#4ABDBD]" />
                                    )}
                                </span>
                                <ChevronRight className={`w-4 h-4 transition-all duration-200 ${
                                    isSelected ? 'text-[#4ABDBD]' : hasValue ? 'text-[#4ABDBD]' : 'text-gray-400'
                                }`} />
                            </button>
                        );
                    })}
                </div>

                {/* Right Panel - Active Section Content */}
                <div className="w-3/5 p-3 overflow-y-auto custom-scrollbar" data-testid="filter-panel-content">
                    {activeSection?.props.children}
                </div>
            </div>

            {/* Action Buttons */}
            <div className="shrink-0 flex justify-end gap-2 pt-3 mt-1 border-t border-gray-200 px-3 pb-3" data-testid="filter-panel-actions">
                <button
                    type="button"
                    onClick={onClear}
                    className="px-4 py-2 rounded-xl shadow-sm bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 hover:shadow-md hover:scale-105 cursor-pointer font-semibold text-sm transition-all duration-200"
                    data-testid="filter-panel-clear-button"
                >
                    {clearLabel}
                </button>
                <button
                    type="button"
                    onClick={onApply}
                    className="px-4 py-2 rounded-xl shadow-sm bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white hover:shadow-md hover:scale-105 cursor-pointer font-semibold text-sm transition-all duration-200"
                    data-testid="filter-panel-apply-button"
                >
                    {applyLabel}
                </button>
            </div>
        </div>
    );
};

export default FilterPanel;
